<template>
  <div id="phenix">
    <!-- Main Header -->
    <b-navbar
      type="dark"
      variant="primary"
      id="main-header"
      sticky>
      <b-navbar-brand
        class="d-flex align-items-center"
        to="/">
        <h3 class="mt-1">Corteva Phenix Admin</h3>
      </b-navbar-brand>

      <b-navbar-nav
        id="main-routes"
        class="d-flex align-items-center"
        v-if="user.UserWebToken">
        <b-nav-item
          to="/customers"
          :class="{
            active: $route.path.includes('customers')
          }">
          <font-awesome-icon icon="users" size="lg"></font-awesome-icon> Customers
        </b-nav-item>
        <b-nav-item
          to="/licenses"
          class="ml-2"
          :class="{
            active: $route.path.includes('licenses')
          }">
          <font-awesome-icon icon="id-card" size="lg"></font-awesome-icon> All Licenses
        </b-nav-item>
        <b-nav-item
          to="/software-updates"
          class="ml-2"
          :class="{
            active: $route.path.includes('software-updates')
          }">
          <font-awesome-icon icon="cloud-upload-alt" size="lg"></font-awesome-icon> Software Updates
        </b-nav-item>
      </b-navbar-nav>

      <b-navbar-nav
        class="ml-auto"
        v-if="user.UserWebToken">
        <b-nav-item-dropdown right>
          <template slot="button-content">
            <font-awesome-icon icon="user" size="lg"></font-awesome-icon> Admin
          </template>
          <b-dropdown-item
            href="#"
            v-on:click="signOut()">
            Sign Out
          </b-dropdown-item>
        </b-nav-item-dropdown>
      </b-navbar-nav>
    </b-navbar>
    <!-- / Main Header -->

    <div id="main-content" class="d-flex">
      <!-- Page Content -->
      <router-view
        class="page-content"
        v-if="enableRouting"
      />
      <!-- / Page Content -->
    </div>

    <!-- Loading Indicator -->
    <Loader
      id="global-loading"
      :msg="globalLoadingMsg">
    </Loader>
    <!-- / Loading Indicator -->

    <!-- Confirm Dialog -->
    <ConfirmDialog />
    <!-- / Confirm Dialog -->

    <!-- Warning Dialog -->
    <b-modal
      id="warning-modal"
      ref="warningModal"
      class="no-title"
      v-model="$root.toggleWarning"
      hide-footer>
      <div>{{$root.warningMsg}}</div>

      <div class="mt-3 d-flex justify-content-end">
        <b-button
          type="button"
          size="sm"
          variant="primary"
          @click="$root.warningMsg = ''; $refs.warningModal.hide();">
          OK
          <font-awesome-icon icon="check-circle" size="sm"></font-awesome-icon>
        </b-button>
      </div>
    </b-modal>
    <!-- / Warning Dialog -->
  </div>
</template>

<script>
import axios from 'axios';
import { EventBus, GlobalLoaderMixin } from './services/event-bus';
import { SessionMixin, Session } from './services/user-session';
import mixins from './services/mixins';

const noAuthRoutes = [
  '/signin',
];

export default {
  name: 'App',
  mixins: [
    GlobalLoaderMixin,
    SessionMixin,
    mixins,
  ],
  data() {
    return {
      globalLoadingMsg: '',
    };
  },
  computed: {
    // enable routing if there is a logged in user or if the route is signin
    enableRouting() {
      return this.user.UserWebToken || noAuthRoutes.includes(this.$route.path);
    },
  },
  methods: {
    signOut() {
      this.$router.replace({ name: 'signin' });
      Session.end();
      this.user = {};
    },
  },
  watch: {
    $route(newRoute) {
      // ensure there's always a user present otherwise force a sign in
      if (!this.user.UserWebToken && !noAuthRoutes.includes(newRoute.path)) {
        this.$router.replace({ name: 'signin' });
      }
    },
  },
  mounted() {
    // register global loading indicator events
    EventBus.$on('show-global-loader', (msg) => { this.globalLoadingMsg = msg; });
    EventBus.$on('hide-global-loader', () => { this.globalLoadingMsg = false; });
  },
  created() {
    // handle API errors
    axios.interceptors.response.use(
      response => response,
      (error) => {
        const r = error.response;

        // handle error
        if (r && r.data && r.data.error) {
          this.showWarning(r.data.error);
        }

        return Promise.reject(r);
      },
    );

    // initialize session
    const sessionPromise = Session.resume();

    // this.showGlobalLoader('Initializing...');

    sessionPromise
      .catch((err) => {
        this.$router.replace({ name: 'signin' });
        return Promise.reject(err);
      })
      .then((user) => {
        this.user = user;
      })
      .finally(() => {
        // this.hideGlobalLoader();
      });
  },
};
</script>

<style lang="scss">
#phenix {
  height: 100%;
}

#main-header {
  height: 48px;

  a.nav-link {
    color: white;
    padding: 0.3rem 0.5rem;
    transition: background-color 0.3s;
  }

  #main-routes {
    a.nav-link {
      border: 1px solid transparent;
      &:hover {
        background-color: rgba(0,0,0,0.1);
      }
    }

    li.nav-item.active {
      a.nav-link {
        border: 1px solid rgba(0,0,0,0.2);
        background-color: rgba(0,0,0,0.2);
      }
    }
  }
}

#main-content {
  height: calc(100vh - 48px);
}

#main-navigation {}

.page-content {
  width: 100vw;
}
</style>
